import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BillingClass,
  CreateBillingClassInput,
  PaginatedResult,
  SortDirection,
  UpdateBillingClassInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface BillingClassListParams {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const billingClassKeys = {
  all: ['billing-classes'] as const,
  list: (params: BillingClassListParams) => ['billing-classes', 'list', params] as const,
  detail: (id: string) => ['billing-classes', 'detail', id] as const,
};

export function useBillingClasses(
  params: BillingClassListParams,
): UseQueryResult<PaginatedResult<BillingClass>> {
  return useQuery({
    queryKey: billingClassKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<BillingClass>>(
        `/billing-classes${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          isActive: params.isActive,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

/**
 * Active billing classes for selects. Authenticated-only endpoint (any user) — use this
 * instead of {@link useBillingClasses} in project/phase forms so non-admins can populate
 * the dropdown.
 */
export function useBillingClassOptions(): UseQueryResult<BillingClass[]> {
  return useQuery({
    queryKey: ['billing-classes', 'options'] as const,
    queryFn: () => api.get<BillingClass[]>('/billing-classes/options'),
  });
}

export function useCreateBillingClass(): UseMutationResult<
  BillingClass,
  unknown,
  CreateBillingClassInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBillingClassInput) =>
      api.post<BillingClass>('/billing-classes', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingClassKeys.all });
    },
  });
}

export interface UpdateBillingClassArgs {
  id: string;
  input: UpdateBillingClassInput;
}

export function useUpdateBillingClass(): UseMutationResult<
  BillingClass,
  unknown,
  UpdateBillingClassArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateBillingClassArgs) =>
      api.patch<BillingClass>(`/billing-classes/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingClassKeys.all });
    },
  });
}

export function useDeleteBillingClass(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/billing-classes/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingClassKeys.all });
    },
  });
}
